import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Download } from '@mui/icons-material';

import {
  buildCsv,
  downloadBlobFile,
  downloadTextFile,
  getDefaultReportPeriod,
  getReportErrorMessage,
  ReportEmptyState,
  ReportErrorState,
  ReportPeriod,
  ReportPeriodFilter,
  ReportsNavigation,
  toApiPeriodParams,
} from '../../components/reports/ReportFilters';
import reportsService, { ContentPlaybackReport } from '../../services/reportsService';

const ContentPlaybackPage: React.FC = () => {
  const [rows, setRows] = useState<ContentPlaybackReport[]>([]);
  const [period, setPeriod] = useState<ReportPeriod>(() => getDefaultReportPeriod());
  const [appliedPeriod, setAppliedPeriod] = useState<ReportPeriod>(() => getDefaultReportPeriod());
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const response = await reportsService.getContentPlayback(toApiPeriodParams(appliedPeriod));
      setRows(response.data);
      setError(null);
    } catch (err) {
      setError(getReportErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [appliedPeriod]);

  useEffect(() => {
    load();
  }, [load]);

  const exportCsv = () => {
    const csv = buildCsv(rows, [
      { header: 'content_id', value: (row) => row.content_id },
      { header: 'content_name', value: (row) => row.content_name },
      { header: 'content_type', value: (row) => row.content_type },
      { header: 'total_plays', value: (row) => row.total_plays },
      { header: 'total_duration_hours', value: (row) => row.total_duration },
      { header: 'average_duration_seconds', value: (row) => row.average_duration_seconds ?? row.average_duration },
      { header: 'completion_rate', value: (row) => row.completion_rate },
      { header: 'unique_devices', value: (row) => row.unique_devices },
    ]);
    downloadTextFile(csv, `content-playback-${appliedPeriod.dateFrom}-${appliedPeriod.dateTo}.csv`);
  };

  const exportExcel = async () => {
    try {
      setExporting(true);
      const file = await reportsService.exportReport('content-playback', toApiPeriodParams(appliedPeriod), 'xlsx');
      downloadBlobFile(file, `content-playback-${appliedPeriod.dateFrom}-${appliedPeriod.dateTo}.xlsx`);
      setError(null);
    } catch (err) {
      setError(getReportErrorMessage(err, 'Не удалось экспортировать отчет по контенту в Excel'));
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="h4" fontWeight="bold">Воспроизведение контента</Typography>
          <Typography variant="body2" color="text.secondary">
            Показы, длительность, завершенность и устройства за выбранный период.
          </Typography>
        </Box>

        <ReportsNavigation />

        <ReportPeriodFilter
          period={period}
          onPeriodChange={setPeriod}
          onApply={() => setAppliedPeriod(period)}
          loading={loading}
          extraActions={(
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" startIcon={<Download />} onClick={exportExcel} disabled={loading || exporting || rows.length === 0}>
                Excel
              </Button>
              <Button variant="outlined" startIcon={<Download />} onClick={exportCsv} disabled={loading || rows.length === 0}>
                CSV
              </Button>
            </Stack>
          )}
        />

        {error && <ReportErrorState title="Отчет не загрузился" message={error} onRetry={load} />}

        <Paper>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
          ) : rows.length === 0 ? (
            <ReportEmptyState title="Нет воспроизведений" message="За выбранный период нет данных по показам контента." />
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Контент</TableCell>
                  <TableCell>Тип</TableCell>
                  <TableCell>Показы</TableCell>
                  <TableCell>Длительность, ч</TableCell>
                  <TableCell>Средняя длительность, с</TableCell>
                  <TableCell>Завершенность</TableCell>
                  <TableCell>Устройства</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={`${row.content_type}-${row.content_id}`}>
                    <TableCell>{row.content_name}</TableCell>
                    <TableCell>{row.content_type}</TableCell>
                    <TableCell>{row.total_plays}</TableCell>
                    <TableCell>{row.total_duration.toFixed(2)}</TableCell>
                    <TableCell>{(row.average_duration_seconds ?? row.average_duration).toFixed(1)}</TableCell>
                    <TableCell>{row.completion_rate.toFixed(1)}%</TableCell>
                    <TableCell>{row.unique_devices}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Paper>
      </Stack>
    </Box>
  );
};

export default ContentPlaybackPage;
